import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Group, Stack, Text, Title } from "@mantine/core";
import * as Sentry from "@sentry/react";

export interface AppErrorBoundaryProps {
    children: ReactNode;
}

const DefaultFallback = ({ resetError }: { resetError: () => void }) => {
    const { t } = useTranslation(["common"]);
    const eventId = Sentry.lastEventId();

    return (
        <Alert color="red" radius="md" variant="light" role="alert" aria-live="assertive" style={{ borderRadius: 0, flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Stack spacing="sm">
                <Title order={4}>{t("error_boundary_title")}</Title>
                <Text size="sm">{t("error_boundary_description")}</Text>
                {eventId ? (
                    <Text size="xs" color="dimmed" aria-label={t("sentry_event_id")}>
                        {t("error_boundary_event_id", { eventId })}
                    </Text>
                ) : null}
                <Group spacing="sm">
                    <Button onClick={() => window.location.reload()} aria-label={t("reload_page")}>
                        {t("reload")}
                    </Button>
                    <Button variant="default" onClick={resetError} aria-label={t("try_again")}>
                        {t("try_again")}
                    </Button>
                </Group>
            </Stack>
        </Alert>
    );
};

export const AppErrorBoundary = ({ children }: AppErrorBoundaryProps) => {
    return (
        <Sentry.ErrorBoundary
            showDialog={false}
            fallback={({ resetError }) => <DefaultFallback resetError={resetError} />}
            beforeCapture={(scope) => {
                scope.setTag("errorBoundary", "true");
            }}
        >
            {children}
        </Sentry.ErrorBoundary>
    );
};

